import dayjs from 'dayjs';
import { isNaN, isNumber, toNumber } from 'lodash-es';
import Pinyin from 'js-pinyin';
import { $t, currentLang } from '@/locales';

/**
 * 格式化时间
 *
 * @param timestamp 时间戳
 * @param format dayjs日期格式
 * @returns
 */
export const filterTimeText = (timestamp: any, format = 'YYYY-MM-DD HH:mm:ss') => {
  const num = toNumber(timestamp);
  if (!isNumber(num) || isNaN(num)) return '--';
  return dayjs(num).format(format);
};

export const setCurrentDayStart = (timestamp: number) => {
  // 对开始时间，设置为当天的00:00:00
  const startDate = new Date(timestamp);
  startDate.setHours(0, 0, 0, 0);

  return startDate.getTime();
};

/**
 * 将给定的时间戳设置为当天的结束时间（23:59:59）
 *
 * 此函数接收一个时间戳参数，并将该时间戳对应的日期设置为当天的23:59:59 主要用于统一处理时间范围的结束点，确保时间范围的查询更加精确和一致
 *
 * @param timestamp {number} - 时间戳，表示当天的开始时间
 * @returns {number} - 返回设置为当天23:59:59的时间戳
 */
export const setCurrentDayFinally = (timestamp: number) => {
  // 对结束时间，设置为当天的23:59:59
  const endDate = new Date(timestamp);
  endDate.setHours(23, 59, 59, 999);

  return endDate.getTime();
};

/**
 * 禁止选择过去时间
 *
 * @param ts
 * @returns true 禁止
 */
export const disablePreviousDate = (ts: number) => {
  return ts < Date.now() - 86400000;
};

/**
 * 只能当前七天内
 *
 * @param ts
 * @param type
 * @param range
 * @returns
 */
export const isRangeCurrtt7Day = (ts: number, type: 'start' | 'end') => {
  return !(
    setCurrentDayFinally(ts) <= setCurrentDayFinally(Date.now()) && ts > setCurrentDayStart(Date.now() - 86400000 * 7)
  );
};

/**
 * 只能选择7天内
 *
 * @param ts
 * @param type
 * @param range
 * @returns
 */
export const isRangeDateDisabled = (ts: number, type: 'start' | 'end', range: [number, number] | null) => {
  if (type === 'start' && range !== null) {
    return setCurrentDayFinally(dayjs(range[1]).valueOf()) - dayjs(ts).valueOf() >= 86400000 * 7;
  }
  if (type === 'end' && range !== null) {
    return setCurrentDayFinally(dayjs(ts).day()) - setCurrentDayFinally(dayjs(range[0]).day()) >= 86400000 * 7;
  }
  return false;
};

/**
 * 延迟执行
 *
 * @param time
 * @returns
 */
export const awaitTime = (time: number) => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve(true);
    }, 1000 * time);
  });
};

/**
 * 绘制echart 地图背景图
 *
 * @param url
 * @param ref echart容器实例
 * @returns
 */
export const drawEchartContainerImage = (url: string, ref: any) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // 创建临时元素
      const canvasBackgroundImg = document.createElement('canvas');
      // 重新裁剪图片，适应元素宽高
      canvasBackgroundImg.width = ref.clientWidth;
      canvasBackgroundImg.height = ref.clientHeight;
      const ctx = canvasBackgroundImg.getContext('2d');
      ctx?.drawImage(img, 0, 0, ref.clientWidth, ref.clientHeight);

      resolve(canvasBackgroundImg);
    };
    img.onerror = error => reject(error);
    img.src = url;
  });
};

/**
 * 数字格式化
 *
 * @param num
 * @returns
 */
export const formatLakhNumber = (num: number) => {
  if (num < 10000) {
    return `${num}`;
  }

  if (num < 10000000) {
    return `${(num / 10000).toFixed(2)}`;
  }

  return `${(num / 10000000).toFixed(2)}`;
};

/**
 * 数字格式化
 *
 * @param num
 * @returns
 */
export const formatLakhUnit = (num: number) => {
  if (num < 10000) {
    return '';
  }

  if (num < 10000000) {
    return 'page.data.myriad';
  }

  return 'page.data.million';
};

/**
 * 数字格式化等级 0 -> 2 个，万，百万
 *
 * @param num
 * @returns
 */
export const formatLakhNumberGear = (num: number) => {
  if (num < 10000) {
    return 0;
  }

  if (num < 10000000) {
    return 1;
  }

  return 2;
};

/**
 * 数字格式化
 *
 * @param num
 * @returns
 */
export const formatLakhGearToUnit = (gear: 0 | 1 | 2) => {
  if (gear === 0) {
    return '';
  }

  if (gear === 1) {
    return 'page.data.myriad';
  }

  return 'page.data.million';
};

export const formatLakhGearToNumber = (num: number, gear: 0 | 1 | 2) => {
  if (gear === 0) {
    return `${num}`;
  }

  if (gear === 1) {
    return `${(num / 10000).toFixed(2)}`;
  }

  return `${(num / 10000000).toFixed(2)}`;
};

/**
 * 扁平化数组
 *
 * @param data
 * @param key
 * @returns Array
 */
export const flatArray = (data: any[], key: string) => {
  const result: any[] = [];
  const flat = arr => {
    arr.forEach(item => {
      result.push(item);
      if (item[key] && item[key].length) {
        flat(item[key]);
      }
    });
  };
  flat(data);
  return result;
};

/**
 * 格式化语言列表
 *
 * @param data
 * @returns Array
 */
Pinyin.setOptions({ checkPolyphone: false, charCase: 0 });
// 获取首字母的方法
export const getFirstLetter = (value: string): string => {
  const firstChar = $t(value).charAt(0);
  if (/[\u4E00-\u9FA5]/.test(firstChar)) {
    // 如果是中文，获取拼音首字母
    return Pinyin.getCamelChars(firstChar).toUpperCase();
  }
  // 如果是英文，直接获取第一个字母
  return firstChar.toUpperCase();
};

// 处理并分组语言列表
export const formatGroupedList = langMap => {
  const sortedList = Object.entries(langMap)
    .map(([key, value]) => ({ code: key, value, firstLetter: getFirstLetter(value) }))
    .sort((a, b) => a.firstLetter.localeCompare(b.firstLetter));

  // 按首字母分组
  const grouped = sortedList.reduce((acc: Record<string, any[]>, item) => {
    const letter = item.firstLetter;
    if (!acc[letter]) {
      acc[letter] = [];
    }
    acc[letter].push(item);
    return acc;
  }, {});
  // 将分组转换为数组
  return Object.keys(grouped)
    .sort()
    .map(letter => ({
      letter,
      items: grouped[letter]
    }));
};

/**
 * 格式化路由code
 *
 * @param data
 * @returns Array
 */
export const addRouteStr = (arr, parentRoute = '') => {
  return arr.map(item => {
    const currentRoute = parentRoute ? `${parentRoute}_${item.menuId}` : `route.${item.menuId}`;
    const updatedItem = { ...item, routeCode: currentRoute };
    if (item.childTrees && item.childTrees.length > 0) {
      updatedItem.childTrees = addRouteStr(item.childTrees, currentRoute);
    }
    return updatedItem;
  });
};

/**
 * @param content
 * @param fileName
 * @returns
 */
export const downloadFile = (content: string | Blob, fileName: string): void => {
  const blob = typeof content === 'string' ? new Blob([content], { type: 'application/octet-stream' }) : content;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url); // 释放 URL 对象
  }, 100);
};

/**
 * 处理标准品和产品多语言数据
 *
 * @param codeArr
 * @returns Array
 */
export const formatFuncPointList = (codeArr: any[]) => {
  const newCodeArr = [];
  let itemInfo = {};
  let itemArr = [];
  codeArr.forEach((v, i) => {
    const { functionDefinition } = v;
    const functionDefinitionObj = functionDefinition ? JSON.parse(functionDefinition) : {};
    const valueDefinitions = functionDefinitionObj?.dataTypeDefinition?.valueDefinitions;
    const dataType = functionDefinitionObj?.dataTypeDefinition?.dataType;
    itemInfo = {
      functionName: v.functionName,
      signCode: v.signCode,
      tabId: 3,
      functionId: v.functionId,
      dpValues: {
        zh_CN: '',
        en: ''
      }
    };
    switch (dataType) {
      case 5:
        newCodeArr.push({ ...itemInfo, code: `function_${v.signCode}` });
        valueDefinitions &&
          valueDefinitions.map((v1, i1) => {
            newCodeArr.push({ ...itemInfo, code: `function_${v.signCode}_${v1.value}` });
          });
        break;
      case 8:
        newCodeArr.push({ ...itemInfo, code: `function_${v.signCode}` });
        valueDefinitions &&
          valueDefinitions.map((v1, i1) => {
            newCodeArr.push({
              ...itemInfo,
              code: v1.value == 1 ? `function_${v.signCode}_on` : `function_${v.signCode}_off`
            });
          });
        break;
      case 2:
      case 1:
      case 3:
        itemArr = [
          { ...itemInfo, code: `function_${v.signCode}` },
          { ...itemInfo, code: `function_${v.signCode}_unit` }
        ];
        newCodeArr.push(...itemArr);
        break;
      default:
        itemArr = {
          ...itemInfo,
          code: `function_${v.signCode}`
        };
        newCodeArr.push(itemArr);
        break;
    }
  });
  return newCodeArr;
};

/**
 * 获取相同名称的最后一行的索引
 *
 * @param data
 * @param index
 * @param signCode
 * @param types
 * @returns number
 */
export const getLastIndex = (data, index, names, types) => {
  const sameNameIndex = data.slice(index).findIndex(item => {
    return names.some((name, i) => item[types[i]] !== name);
  });
  return sameNameIndex === -1 ? data.length : sameNameIndex;
};
/**
 * 处理菜单得多语言文件数据
 *
 * @param data
 * @returns number
 */
export const formatMenuList = (data: any[]) => {
  const localRouteEn = {};
  const localRouteCh = {};
  data?.forEach(v => {
    v.code = v.code.slice(6);
    localRouteEn[v.code] = v.dpValues.en;
    localRouteCh[v.code] = v.dpValues.zh_CN;
  });
  return { localRouteEn, localRouteCh };
};

export const formatMenuListHome = (data: any[]) => {
  const localRouteEn = {};
  const localRouteCh = {};
  data?.forEach(v => {
    const { lang, data } = v;
    for (const key in data.menu) {
      const newKey = key.slice(6);
      if (lang === 'en') {
        localRouteEn[newKey] = data.menu[key];
      }
      if (lang === 'zh_CN') {
        localRouteCh[newKey] = data.menu[key];
      }
    }
  });
  return { localRouteEn, localRouteCh };
};

export const formatFirmwareTypeList = (data: any[], typeList: any[]) => {
  const enData = data.find(item => item.lang === 'en');
  const zhCNData = data.find(item => item.lang === 'zh_CN');
  typeList.forEach(i => {
    const code = `firmware_type_${i.id}`;
    i.en = enData?.data?.firmwareType?.[code] ?? '';
    i.zh = zhCNData?.data?.firmwareType?.[code] ?? '';
    i.no = i.no.toString();
  });

  return { typeList };
};

export const getShowCodeContent = (data: any) => {
  let lang = currentLang.value;
  if (lang === 'en_US') {
    lang = 'en';
  }

  if (data.dpValues?.[lang]) {
    return data.dpValues[lang];
  }

  return data.keyName;
};
